import { type V2, lerp, smoothstep } from '../core/math';
import { fbm2 } from './build';
import type { ZoneBuilder } from './ZoneBuilder';

/**
 * Helpers for island zones (Haven Isle, Glimmer Isle, the Gleaming Sea): a
 * coastline defined by a radius that varies with the bearing, a land profile
 * that falls to the sea floor across a beach, and plank piers standing on a
 * thin causeway just above the water so they are walkable.
 */

export interface Coast {
  /** Centre of the island. */
  x: number;
  z: number;
  /** Mean shoreline radius. */
  r: number;
  /** Harmonics of the shoreline: [frequency, amplitude (fraction of r), phase]. */
  waves: [number, number, number][];
}

/** Bearing of `p` seen from the island centre (facing-angle convention). */
export const bearing = (c: V2, x: number, z: number): number => Math.atan2(x - c.x, z - c.z);

/** Shoreline radius at a bearing. */
export function shoreRadius(c: Coast, a: number): number {
  let k = 1;
  for (const [f, amp, ph] of c.waves) k += amp * Math.sin(f * a + ph);
  return c.r * k;
}

/** Signed distance (approx.) to the shoreline: negative inland. */
export function shoreDistance(c: Coast, x: number, z: number): number {
  return Math.hypot(x - c.x, z - c.z) - shoreRadius(c, bearing(c, x, z));
}

/** Point on the shoreline at a bearing, pushed `out` metres seaward. */
export function shorePoint(c: Coast, a: number, out = 0): V2 {
  const r = shoreRadius(c, a) + out;
  return { x: c.x + Math.sin(a) * r, z: c.z + Math.cos(a) * r };
}

export interface LandProfile {
  seaFloor: number;
  /** Land height just behind the beach. */
  shore: number;
  /** Extra height reached `inland` metres from the shore. */
  rise: number;
  inland: number;
  /** Beach width either side of the shoreline. */
  beach: number;
  hills: number;
  seed: number;
}

/** Terrain height of one island (sea floor far out, rising inland with noise). */
export function landHeight(c: Coast, p: LandProfile, x: number, z: number): number {
  const d = shoreDistance(c, x, z);
  if (d > p.beach * 2 + 4) return p.seaFloor;
  const land = 1 - smoothstep(-p.beach, p.beach * 1.5, d);
  const inland = smoothstep(0, p.inland, -d);
  const hills = (fbm2(x * 0.035, z * 0.035, p.seed, 3) - 0.5) * 2 * p.hills * smoothstep(2, 16, -d);
  return lerp(p.seaFloor, p.shore + p.rise * inland + hills, land);
}

export interface Pier {
  /** Landward end (on the beach). */
  from: V2;
  /** Facing angle pointing out to sea. */
  dir: number;
  length: number;
  /** Half-width of the walkable causeway under the planks. */
  halfWidth: number;
}

/** Tip of a pier. */
export function pierTip(p: Pier, extra = 0): V2 {
  return { x: p.from.x + Math.sin(p.dir) * (p.length + extra), z: p.from.z + Math.cos(p.dir) * (p.length + extra) };
}

/** 0..1 mask of the causeway under a pier. */
export function pierMask(p: Pier, x: number, z: number): number {
  const ux = Math.sin(p.dir);
  const uz = Math.cos(p.dir);
  const dx = x - p.from.x;
  const dz = z - p.from.z;
  const along = dx * ux + dz * uz;
  const across = Math.abs(dx * uz - dz * ux);
  return (1 - smoothstep(p.halfWidth, p.halfWidth + 0.5, across)) * smoothstep(-2, 0, along) * (1 - smoothstep(p.length, p.length + 0.6, along));
}

/** Raises terrain to a deck just above the water where the pier stands. */
export function withPier(h: number, p: Pier, deck: number, x: number, z: number): number {
  const m = pierMask(p, x, z);
  return m > 0 ? Math.max(h, lerp(h, deck, m)) : h;
}

/** Height offset so a prop's origin sits at `level + lift` over the terrain below it. */
export function floatY(b: ZoneBuilder, x: number, z: number, level: number, lift = 0): number {
  return level + lift - b.heightAt(x, z);
}

/** Lays `dock_segment` planking (6 m pieces, deck 1.2 m above the model origin) along a pier. */
export function layPier(b: ZoneBuilder, p: Pier, deck: number): void {
  const n = Math.max(1, Math.round(p.length / 6));
  for (let i = 0; i < n; i++) {
    const t = ((i + 0.5) / n) * p.length;
    const x = p.from.x + Math.sin(p.dir) * t;
    const z = p.from.z + Math.cos(p.dir) * t;
    b.prop('dock_segment', x, z, { rot: p.dir, y: floatY(b, x, z, deck, -1.2), scale: p.halfWidth > 1.6 ? 1.2 : 1 });
  }
  const tip = pierTip(p, -0.8);
  const side = { x: Math.cos(p.dir), z: -Math.sin(p.dir) };
  for (const s of [-1, 1]) {
    const x = tip.x + side.x * (p.halfWidth - 0.2) * s;
    const z = tip.z + side.z * (p.halfWidth - 0.2) * s;
    b.prop('lamp_post', x, z, { y: floatY(b, x, z, deck) });
  }
}
